import { storedIdemKey, clearIdemKey, idemConfig } from '../lib/idempotency';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sayfalı bir listeyi tüm sayfalarıyla yükler
export const fetchAllPages = async (path) => {
  const all = [];
  for (let page = 1; ; page++) {
    const { data } = await axios.get(`${API}${path}${path.includes('?') ? '&' : '?'}page_size=500&page=${page}`);
    all.push(...(data.items || []));
    if (page >= (data.total_pages || 1)) return all;
  }
};

// Yalnızca verilen id'lerin kayıtlarını 200'lük parçalarla getirir (yetki kapsamı backend'de)
const fetchByIds = async (path, ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
  const res = await Promise.all(chunks.map(c => axios.get(`${API}${path}?ids=${c.map(encodeURIComponent).join(',')}&page_size=500&include_archived=true${path === '/employees' ? '&masked=true' : ''}`)));
  return res.flatMap(r => r.data.items || []);
};

export const useWorkPermits = (hasLocationAccess, defaultStatus = 'aktif') => {
  const [permits, setPermits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permitsReady, setPermitsReady] = useState(false);

  // Filters
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [searchQuery, setSearchQuery] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');

  const permitSeq = useRef(0);
  const refIds = useRef({ employees: new Set(), equipment: new Set() });

  // Listedeki izinlerde geçen personel/ekipmanı (henüz yüklenmemişse) getir
  const loadRefs = useCallback(async (list, force = false) => {
    const pick = (key, kind) => [...new Set(list.flatMap(p => p[key] || []))].filter(id => force || !refIds.current[kind].has(id));
    const empIds = pick('employee_ids', 'employees');
    const eqIds = pick('equipment_ids', 'equipment');
    const [emps, eqs] = await Promise.all([
      empIds.length ? fetchByIds('/employees', empIds) : [],
      eqIds.length ? fetchByIds('/equipment', eqIds) : [],
    ]);
    const merge = (prev, items, kind) => {
      items.forEach(x => refIds.current[kind].add(x.id));
      const map = new Map(prev.map(x => [x.id, x]));
      items.forEach(x => map.set(x.id, x));
      return [...map.values()];
    };
    if (emps.length) setAllEmployees(prev => merge(prev, emps, 'employees'));
    if (eqs.length) setAllEquipment(prev => merge(prev, eqs, 'equipment'));
    return eqs;
  }, []);

  const fetchPermits = useCallback(async () => {
    const seq = ++permitSeq.current;
    try {
      let params = ['fields=list'];
      if (selectedLocationFilter !== 'all') params.push(`location_id=${selectedLocationFilter}`);
      if (selectedCompanyFilter !== 'all') params.push(`company_id=${selectedCompanyFilter}`);
      // Tüm yetkili izinler (hafif liste yanıtı); filtre/arama/sayfalama mevcut kurallarla tarayıcıda
      const data = await fetchAllPages(`/work-permits?${params.join('&')}`);
      if (seq !== permitSeq.current) return;
      setPermits(data);
      setPermitsReady(true);
      await loadRefs(data);
    } catch (error) {
      if (seq !== permitSeq.current) return;
      setPermitsReady(true);
      if (navigator.onLine) toast.error('Çalışma izinleri yüklenemedi');
    }
  }, [selectedLocationFilter, selectedCompanyFilter, loadRefs]);

  const fetchData = useCallback(async () => {
    try {
      const [companiesAll, locationsRes] = await Promise.all([
        fetchAllPages('/companies'),
        axios.get(`${API}/locations`)
      ]);
      setCompanies(companiesAll);
      setLocations(locationsRes.data);
    } catch (error) {
      if (navigator.onLine) toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Durum filtresi tarayıcıda uygulanır; yalnız lokasyon/firma değişince yeniden yüklenir
  useEffect(() => {
    fetchPermits();
  }, [fetchPermits]);

  const fetchEmployeesForCompany = async (companyId) => {
    try {
      const response = await axios.get(`${API}/employees?company_id=${companyId}&masked=true`);
      return response.data.items || response.data || [];
    } catch (error) {
      console.error('Employees fetch error:', error);
      return [];
    }
  };

  // Ekipman verisini yenile (detay modalı için)
  const refreshEquipment = async (permit) => {
    try {
      return await loadRefs(permit ? [permit] : permits, true);
    } catch (error) {
      console.error('Equipment refresh error:', error);
      return allEquipment;
    }
  };

  const createPermit = async (formData) => {
    await axios.post(`${API}/work-permits`, formData, idemConfig(storedIdemKey('wp-legacy-create-idem')));
    clearIdemKey('wp-legacy-create-idem');
    await fetchPermits();
  };

  const updatePermit = async (id, formData) => {
    await axios.put(`${API}/work-permits/${id}`, formData);
    await fetchPermits();
  };

  const deletePermit = async (id) => {
    await axios.delete(`${API}/work-permits/${id}`);
    await fetchPermits();
  };

  // Helper functions
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '-';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || '-';
  };

  const getEmployeeById = (employeeId) => {
    return allEmployees.find(e => e.id === employeeId);
  };

  const getCompaniesForLocation = (locationId) => {
    if (!locationId) return [];
    return companies.filter(c => c.location_id === locationId);
  };

  const getEquipmentForCompany = (companyId) => {
    if (!Array.isArray(allEquipment)) return [];
    return allEquipment.filter(eq => eq.company_id === companyId && eq.is_active !== false);
  };

  // Kullanıcının yetkili olduğu lokasyonları filtrele
  const authorizedLocations = locations.filter(loc => hasLocationAccess(loc.id));

  return {
    // Data
    permits,
    companies,
    locations,
    allEmployees,
    allEquipment,
    authorizedLocations,
    loading: loading || !permitsReady,
    
    // Filters
    selectedLocationFilter,
    setSelectedLocationFilter,
    selectedCompanyFilter,
    setSelectedCompanyFilter,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    companySearchQuery,
    setCompanySearchQuery,
    
    // Actions
    fetchData,
    fetchPermits,
    fetchEmployeesForCompany,
    refreshEquipment,
    createPermit,
    updatePermit,
    deletePermit,
    
    // Helpers
    getCompanyName,
    getLocationName,
    getEmployeeById,
    getCompaniesForLocation,
    getEquipmentForCompany
  };
};
